import type {ComponentProps, FC} from 'react';
import type {
	PortableTextComponentProps,
	PortableTextMarkComponentProps,
} from '@portabletext/react';
import {PortableText as PortableTextReact} from '@portabletext/react';

import type {BlockTheme} from '@/components/content-blocks/block-container';
import {imageWebFromImageWebSchema} from '@/components/image/query';
import type {LinkFragment} from '@/components/link/query';
import {Blockquote} from '@/components/portable-text/blockquote';
import {BulletList} from '@/components/portable-text/bullet-list';
import {CallToActionLine} from '@/components/portable-text/call-to-action-line/CallToActionLine';
import type {CallToActionLineFragment} from '@/components/portable-text/call-to-action-line/query';
import {NumberList} from '@/components/portable-text/number-list';
import {PortableImage} from '@/components/portable-text/portable-image';
import type {PortableTextGalleryProps} from '@/components/portable-text/portable-text-gallery';
import {PortableTextGallery} from '@/components/portable-text/portable-text-gallery';
import {Video} from '@/components/portable-text/video';
import {Heading} from '@/components/typography/heading';
import {Paragraph} from '@/components/typography/paragraph';
import {cn} from '@/utils/tailwind';

import type {VideoFragment} from './video/schema';
import type {PortableLinkProps} from './portable-link';
import {PortableLink} from './portable-link';

/**
 * Override class names for portable text components
 */
export type PortableTextOverrides = {
	ul?: string;
	ol?: string;
};

type SanityPortableTextProps = ComponentProps<typeof PortableTextReact>;

interface Props extends SanityPortableTextProps {
	overrideClassNames?: PortableTextOverrides;
	theme?: BlockTheme;
	slotProps?: {
		normal?: ComponentProps<typeof Paragraph>;
		h2?: ComponentProps<typeof Heading>;
		h3?: ComponentProps<typeof Heading>;
		h4?: ComponentProps<typeof Heading>;
		h5?: ComponentProps<typeof Heading>;
		h6?: ComponentProps<typeof Heading>;
		link?: Omit<PortableLinkProps, 'children'>;
		// blockquote?: ComponentProps<'blockquote'>;
		// listBullet?: ComponentProps<typeof ListUnordered>;
		// listNumber?: ComponentProps<typeof ListOrdered>;
		// listItemBullet?: ComponentProps<typeof ListItem>;
		// listItemNumber?: ComponentProps<typeof ListItem>;
		callToActionLine?: Omit<
			ComponentProps<typeof CallToActionLine>,
			'value'
		>;
	};
}

export const PortableText: FC<Props> = ({
	value,
	slotProps,
	overrideClassNames,
}) => {
	return (
		<PortableTextReact
			value={value}
			components={{
				block: {
					normal: ({children}) => {
						const {className, ...restProps} =
							slotProps?.normal ?? {};
						return (
							<Paragraph
								className={cn(['mb-6', 'last:mb-0', className])}
								{...restProps}
							>
								{children}
							</Paragraph>
						);
					},
					large: ({children}) => {
						const {className, ...restProps} =
							slotProps?.normal ?? {};
						return (
							<Paragraph
								size="3xlarge"
								className={cn([
									'mb-6',
									'leading-normal',
									'last:mb-0',
									className,
								])}
								{...restProps}
							>
								{children}
							</Paragraph>
						);
					},
					blockquote: ({children}) => (
						<Blockquote>{children}</Blockquote>
					),
					h2: ({children}) => {
						const {
							className,
							level,
							spacing = true,
							...restProps
						} = slotProps?.h2 ?? {};
						return (
							<Heading
								level={level ?? 2}
								spacing={spacing}
								className={cn([''], className)}
								{...restProps}
							>
								{children}
							</Heading>
						);
					},
					h3: ({children}) => {
						const {
							className,
							level,
							spacing = true,
							...restProps
						} = slotProps?.h3 ?? {};
						return (
							<Heading
								level={level ?? 3}
								spacing={spacing}
								className={cn([''], className)}
								{...restProps}
							>
								{children}
							</Heading>
						);
					},
					h4: ({children}) => {
						const {
							className,
							level,
							spacing = true,
							...restProps
						} = slotProps?.h4 ?? {};
						return (
							<Heading
								level={level ?? 4}
								spacing={spacing}
								className={cn([''], className)}
								{...restProps}
							>
								{children}
							</Heading>
						);
					},
					h5: ({children}) => {
						const {
							className,
							level,
							spacing = true,
							...restProps
						} = slotProps?.h5 ?? {};
						return (
							<Heading
								level={level ?? 5}
								spacing={spacing}
								className={cn([''], className)}
								{...restProps}
							>
								{children}
							</Heading>
						);
					},
					h6: ({children}) => {
						const {
							className,
							level,
							spacing = true,
							...restProps
						} = slotProps?.h6 ?? {};
						return (
							<Heading
								level={level ?? 6}
								spacing={spacing}
								className={cn([''], className)}
								{...restProps}
							>
								{children}
							</Heading>
						);
					},
				},
				marks: {
					link: ({
						value,
						children,
					}: PortableTextMarkComponentProps<
						LinkFragment & {_type: 'link'}
					>) => {
						if (!value) {
							return null;
						}
						return (
							<PortableLink value={value} {...slotProps?.link}>
								{children}
							</PortableLink>
						);
					},
					linkAnnotation: () => null,
				},
				list: {
					bullet: ({children}) => (
						<BulletList className={overrideClassNames?.ul}>
							{children}
						</BulletList>
					),
					number: ({children}) => (
						<NumberList className={overrideClassNames?.ol}>
							{children}
						</NumberList>
					),
				},
				types: {
					callToActionLine: ({
						value,
					}: PortableTextComponentProps<CallToActionLineFragment>) => (
						<CallToActionLine
							value={value}
							{...slotProps?.callToActionLine}
							buttonProps={{
								...slotProps?.callToActionLine?.buttonProps,
							}}
						/>
					),
					image: ({value}) => {
						if (value.asset && value.asset._ref) {
							return (
								<PortableImage
									image={imageWebFromImageWebSchema(value)}
								/>
							);
						}
					},
					video: ({value}: {value: VideoFragment}) => {
						if (value.id && value.id.length > 0 && value.title) {
							return <Video id={value.id} title={value.title} />;
						}
					},
					portableTextGallery: ({
						value,
					}: PortableTextComponentProps<PortableTextGalleryProps>) => {
						return <PortableTextGallery {...value} />;
					},
				},
			}}
		/>
	);
};
